import React from 'react';
import { Box, Button, Chip, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Text from '../../../components/UI/atoms/Text';

const TAG_COLORS = {
  TNM_NEWS_DATA: 'primary',
  TNM_NEWS_MARKET: 'secondary',
  TNM_BASIC: 'default',
};

const isThisMonth = (dateVal) => {
  if (!dateVal) return false;
  const d = new Date(dateVal);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

const fmtDate = (dateVal) => {
  if (!dateVal) return '—';
  return new Date(dateVal).toLocaleDateString('en-CA'); // YYYY-MM-DD
};

const SortIcon = ({ field, sortBy, sortDir }) => {
  if (sortBy !== field) return <span sx={{ fontSize: '14px', color: 'text.disabled', ml: '2px' }}>⇅</span>;
  return sortDir === 'asc'
    ? <span sx={{ fontSize: '14px', color: 'primary.main', ml: '2px' }}>↑</span>
    : <span sx={{ fontSize: '14px', color: 'primary.main', ml: '2px' }}>↓</span>;
};

export default function FirmTable({ firms, sortBy, sortDir, onSort }) {
  const navigate = useNavigate();

  const sortHeader = (label, field) => (
    <TableCell sx={{ width: '20%', cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort?.(field)}>
      {label}
      <SortIcon field={field} sortBy={sortBy} sortDir={sortDir} />
    </TableCell>
  );

  if (!firms || firms.length === 0) {
    return (
      <Box sx={{ py: '24px', textAlign: 'center' }}>
        <Text sx={{ color: 'text.secondary' }}>No firms found.</Text>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size='small'>
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
            <TableCell sx={{ width: '18%' }}>Firm Name</TableCell>
            <TableCell sx={{ width: '13%' }}>Root User</TableCell>
            <TableCell sx={{ width: '20%' }}>Email</TableCell>
            {sortHeader('Plan', 'plan')}
            {sortHeader('Renewal Date', 'renewalDate')}
            <TableCell sx={{ width: '10%' }}>End Date</TableCell>
            <TableCell align='right' sx={{ width: '4%' }}>Seats</TableCell>
            <TableCell align='right' sx={{ width: '5%' }}>Copies</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {firms.map((f, idx) => {
            const firmId = f?.FirmId || f?.firmId;
            const tags = Array.isArray(f?.SubscriptionTag || f?.subscriptionTag)
              ? (f?.SubscriptionTag || f?.subscriptionTag)
              : [];
            const rootName = [f?.FirstName || f?.firstName, f?.LastName || f?.lastName].filter(Boolean).join(' ') || '—';
            const renewalDate = f?.RenewalDate || f?.renewalDate;
            const endDate = f?.SubscriptionEndDate || f?.subscriptionEndDate;
            const renewalThisMonth = isThisMonth(renewalDate);
            return (
              <TableRow
                key={firmId || idx}
                hover
                onClick={() => navigate(`/admin/firms/${firmId}`)}
                sx={{
                  cursor: 'pointer',
                  '&:last-child td': { borderBottom: 0 },
                  ...(renewalThisMonth && { bgcolor: 'warning.50' }),
                }}
              >
                <TableCell sx={{ fontWeight: 500 }}>{f?.FirmName || f?.firmName}</TableCell>
                <TableCell>{rootName}</TableCell>
                <TableCell sx={{ fontSize: '13px' }}>{f?.Email || f?.email}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {tags.length > 0
                      ? tags.map((t, i) => (
                          <Chip
                            key={i}
                            label={t}
                            size='small'
                            color={TAG_COLORS[t] || 'default'}
                            variant='outlined'
                            sx={{ fontSize: '11px', height: '20px' }}
                          />
                        ))
                      : <Text sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Text>
                    }
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: '13px' }}>
                  {renewalThisMonth ? (
                    <Chip label={fmtDate(renewalDate)} size='small' color='warning' sx={{ fontSize: '11px', height: '20px' }} />
                  ) : fmtDate(renewalDate)}
                </TableCell>
                <TableCell sx={{ fontSize: '13px' }}>{fmtDate(endDate)}</TableCell>
                <TableCell align='right'>{f?.SeatAlloted ?? f?.seatAlloted ?? '—'}</TableCell>
                <TableCell align='right'>{f?.Copies ?? f?.copies ?? '—'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
